import React from 'react';

/**
 * InstabilitySection Component
 * Used by: Shoulder (dislocation tracking), Ankle (giving way episodes)
 * Adapts based on injury type
 */
const InstabilitySection = ({ config, formData, onChange, injuryType }) => {
  const handleFieldChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  const handleCheckboxChange = (fieldId, option) => {
    const currentValues = formData[fieldId] || [];
    const newValues = currentValues.includes(option)
      ? currentValues.filter(v => v !== option)
      : [...currentValues, option];
    onChange(fieldId, newValues);
  };

  const shouldShowField = (field) => {
    if (!field.conditional) return true;
    
    const { field: condField, value: condValue } = field.conditional;
    return formData[condField] === condValue;
  };

  const renderField = (field) => {
    if (!shouldShowField(field)) return null;

    const value = formData[field.id] || '';

    return (
      <div key={field.id} className="field-group">
        <label className="field-label">
          {field.label}
          {field.required && <span className="required">*</span>}
        </label>

        {field.type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="form-input"
          />
        )}

        {field.type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            className="form-textarea"
          />
        )}

        {field.type === 'radio' && (
          <div className="radio-group">
            {field.options.map(option => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'checkbox-multiple' && (
          <div className="checkbox-group">
            {field.options.map(option => (
              <label key={option} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={(value || []).includes(option)}
                  onChange={() => handleCheckboxChange(field.id, option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'select' && (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        )}

        {field.note && (
          <p className="field-note">{field.note}</p>
        )}

        {field.placeholder && (field.type === 'text' || field.type === 'textarea') && (
          <p className="field-hint">{field.placeholder}</p>
        )}
      </div>
    );
  };

  // Determine border color based on injury type
  const getBorderColor = () => {
    switch(injuryType) {
      case 'SHOULDER': return '#f59e0b'; // Orange
      case 'ANKLE': return '#f59e0b'; // Orange
      default: return '#8b5cf6'; // Purple
    }
  };

  const getBackgroundColor = () => {
    switch(injuryType) {
      case 'SHOULDER': return '#fffbeb';
      case 'ANKLE': return '#fffbeb';
      default: return '#faf5ff';
    }
  };

  return (
    <div className="instability-section">
      <style jsx>{`
        .instability-section {
          border: 3px solid ${getBorderColor()};
          border-radius: 8px;
          padding: 24px;
          background: ${getBackgroundColor()};
          margin: 24px 0;
        }

        .section-note {
          background: white;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid ${getBorderColor()};
          font-size: 14px;
          font-weight: 600;
          color: #78350f;
        }

        .field-group {
          background: white;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
        }

        .field-label {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 12px;
          font-size: 15px;
        }

        .required {
          color: #dc2626;
          margin-left: 4px;
        }

        .form-input,
        .form-textarea,
        .form-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .form-textarea {
          resize: vertical;
        }

        .radio-group {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .radio-option,
        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .radio-option:hover,
        .checkbox-option:hover {
          background: #f9fafb;
        }

        .radio-option input,
        .checkbox-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .radio-option span,
        .checkbox-option span {
          font-size: 15px;
          color: #374151;
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .field-note {
          margin-top: 8px;
          font-size: 13px;
          color: #7c3aed;
          font-style: italic;
        }

        .field-hint {
          margin-top: 6px;
          font-size: 12px;
          color: #6b7280;
        }

        @media (max-width: 640px) {
          .instability-section {
            padding: 16px;
          }

          .radio-group {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>

      {config.title && (
        <div className="section-note">
          ⚠️ {config.title} Assessment - {injuryType === 'SHOULDER' ? 'Dislocation History' : 'Chronic Instability'}
        </div>
      )}

      <div className="fields-container">
        {config.fields && config.fields.map(field => renderField(field))}
      </div>
    </div>
  );
};

export default InstabilitySection;
